// Response search/moderation hooks (responses_search.rs, responses_admin.rs)
// + the CSV export (responses_export.rs).
//   POST /v1/surveys/{surveyId}/responses/search
//   GET /v1/responses/{id}
//   DELETE /v1/responses/{id}
//   POST /v1/surveys/{surveyId}/responses/export
//
// Search takes the platform's typed operators — `ResponseSearchFilters`
// mirrors response_filters.rs — through `useResponseSearch`. The export is a
// `POST` of the same body minus the paging, downloaded through `http.postBlob`
// by `downloadResponsesCsv`: a GET carries no body, so there is no export
// link to build.

import type { BoolOp, DurationOp, IdOp, NullableIdOp, NumberOp, TemporalOp, TextOp } from "@pej/kore/components";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "./http";
import { overviewKeys, responseKeys, statsKeys, surveyKeys } from "./keys";
import type { PageRs, SearchRq } from "./search";
import type { ResponseDetail, ResponseRow } from "./types";

export type ResponseSortField = "SubmittedAt" | "Score";
export type ResponseSearchQueryField = "Text";

/** One response with its attributes, for the detail panel; idle while null. */
export function useResponse(responseId: string | null) {
    return useQuery({
        queryKey: responseKeys.detail(responseId ?? ""),
        queryFn: () => http.get<ResponseDetail>(`/v1/responses/${responseId}`),
        enabled: responseId !== null,
    });
}

export function useDeleteResponse(workspaceId: string, surveyId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        // The id arrives at mutate time so one hook instance serves a whole
        // table of rows. Invalidated by key prefix — responseKeys.searchRoot
        // — rather than one exact params tuple: responseKeys.search carries
        // whatever filters/paging the table was showing, and a deleted row
        // should drop out of every one of those cached pages, not just the
        // one the delete button happened to be rendered from. Everything
        // else that shows the count moves with it: the survey (its danger
        // zone's Delete rule), the surveys list (count and row Delete),
        // the survey's stats and the workspace overview.
        mutationFn: (responseId: string) => http.delete<void>(`/v1/responses/${responseId}`),
        onSuccess: () =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: responseKeys.searchRoot(surveyId) }),
                queryClient.invalidateQueries({ queryKey: surveyKeys.detail(surveyId) }),
                queryClient.invalidateQueries({ queryKey: surveyKeys.searchRoot(workspaceId) }),
                queryClient.invalidateQueries({ queryKey: statsKeys.root(surveyId) }),
                queryClient.invalidateQueries({ queryKey: overviewKeys.root(workspaceId) }),
            ]),
    });
}

/** One step's answer filter, by option key — `response_filters.rs`'s
 * `StepAnswerFilter`. `op` is an `EnumOp<String>` server-side; spelled out
 * here rather than imported generic, matching the wire shape verbatim. */
export interface StepAnswerFilter {
    stepId: string;
    op: { type: "Eq" | "Neq"; values: string[] };
}

/** response_filters.rs's filter struct — the typed vocabulary. */
export interface ResponseSearchFilters {
    submittedAt?: TemporalOp;
    submittedWithin?: DurationOp;
    placement?: IdOp;
    score?: NumberOp;
    answers?: StepAnswerFilter[];
    hasText?: BoolOp;
    contextTrusted?: BoolOp;
    completed?: BoolOp;
    locationRef?: TextOp;
    respondentRef?: NullableIdOp;
    reference?: TextOp;
}

export type ResponseSearchRq = SearchRq<ResponseSearchFilters, ResponseSortField, ResponseSearchQueryField>;
/** The export's own request: the same filters/query as search, no paging —
 * a CSV export is the whole matching set in one shot. */
export type ResponseExportRq = Omit<ResponseSearchRq, "pageToken" | "pageSize">;

export function useResponseSearch(surveyId: string, rq: ResponseSearchRq | null) {
    return useQuery({
        queryKey: responseKeys.search(surveyId, rq),
        queryFn: () => http.post<PageRs<ResponseRow>>(`/v1/surveys/${surveyId}/responses/search`, { json: rq }),
        enabled: Boolean(surveyId) && rq !== null,
        // A changed filter or page is a new key; keep the previous rows on
        // screen while it loads rather than flashing the table's loading state.
        placeholderData: keepPreviousData,
    });
}

/** The export is a file: posted like a search, then handed to the browser
 * as a download. `window.open` can't carry a request body. */
export async function downloadResponsesCsv(surveyId: string, rq: ResponseExportRq): Promise<{ truncated: boolean }> {
    const { blob, headers } = await http.postBlob(`/v1/surveys/${surveyId}/responses/export`, { json: rq });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "responses.csv";
    // In the page while it's clicked, and the URL freed a tick later rather
    // than straight after the click: WebKit can drop a download from a
    // detached link or one whose URL is revoked in the same task.
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return { truncated: headers.get("X-Truncated") === "true" };
}
